import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  BASE_CURRENCY,
  CURRENCY_SIGN,
  formatMoney,
  isCurrency,
  type Currency,
} from 'shared';
import { api, type CurrencyOption } from './api';

/**
 * Деньги на экране.
 *
 * Три разные вещи, которые до сих пор путались в одну:
 *
 * 1. **Валюта сделки** — то, о чём договорились стороны. Единственная
 *    настоящая сумма. Заявка в тенге и есть заявка в тенге.
 * 2. **Валюта учёта** — рубль. В нём хранятся все суммы площадки, в нём
 *    считается наценка, в нём сравниваются заявки между собой.
 * 3. **Валюта отображения** — в чём этот человек хочет видеть цифры.
 *    Только вид: сумму сделки она не меняет никогда.
 *
 * Отсюда правило показа: **основная строка — сумма сделки**, пересчёт
 * идёт рядом со знаком «≈» и мельче. Человек должен видеть то, что
 * подпишет, а не то, что ему удобнее читать.
 */

interface MoneyCtx {
  /** Включённые валюты с курсами. Пусто, пока не загрузились. */
  options: CurrencyOption[];
  /** В чём человек хочет видеть суммы. Пусто — в валюте сделки. */
  display: Currency | null;
  setDisplay: (c: Currency | null) => void;
  /** Курс к рублю, или null если валюта неизвестна. */
  rate: (c: Currency) => number | null;
}

const Ctx = createContext<MoneyCtx>({
  options: [],
  display: null,
  setDisplay: () => {},
  rate: (c) => (c === BASE_CURRENCY ? 1 : null),
});

export function MoneyProvider({
  displayCurrency,
  children,
}: {
  displayCurrency: string | null;
  children: ReactNode;
}) {
  const [options, setOptions] = useState<CurrencyOption[]>([]);
  const [display, setDisplay] = useState<Currency | null>(
    isCurrency(displayCurrency) ? displayCurrency : null,
  );

  useEffect(() => {
    setDisplay(isCurrency(displayCurrency) ? displayCurrency : null);
  }, [displayCurrency]);

  useEffect(() => {
    void api
      .currencies()
      .then((r) => setOptions(r.items))
      // Валюты не загрузились — показываем суммы как есть, в валюте
      // сделки. Это ровно то, что было до появления пересчёта, и это
      // честно: непересчитанная сумма верна, пересчитанная наугад — нет.
      .catch(() => setOptions([]));
  }, []);

  const rate = (c: Currency): number | null => {
    if (c === BASE_CURRENCY) return 1;
    const found = options.find((o) => o.code === c);
    return found?.rate ?? null;
  };

  return (
    <Ctx.Provider value={{ options, display, setDisplay, rate }}>{children}</Ctx.Provider>
  );
}

export function useMoney() {
  return useContext(Ctx);
}

/**
 * Сумма на экране.
 *
 * `value` — в валюте `currency`. Если человек выбрал валюту отображения и
 * она другая, под суммой появляется пересчёт со знаком «≈».
 *
 * `frozen` — сумма заключённой сделки. У неё пересчёт не показывается
 * вовсе: сделка зафиксирована, и подставлять рядом сегодняшний курс
 * значит намекать, что сумма может измениться. Она не может.
 */
export function Money({
  value,
  currency = BASE_CURRENCY,
  frozen = false,
  className,
}: {
  value: number;
  currency?: Currency;
  frozen?: boolean;
  className?: string;
}) {
  const { display, rate } = useMoney();

  const main = formatMoney(value, currency);
  if (frozen || !display || display === currency) {
    return <span className={className}>{main}</span>;
  }

  const from = rate(currency);
  const to = rate(display);
  if (from === null || to === null || to <= 0) {
    return <span className={className}>{main}</span>;
  }

  return (
    <span className={className}>
      {main}
      <span className="money__alt"> ≈ {formatMoney((value * from) / to, display)}</span>
    </span>
  );
}

/** Знак валюты для подписи поля ввода: «Ставка, ₸». */
export function signOf(c: Currency): string {
  return CURRENCY_SIGN[c];
}
